/**
 * Shared Zod schemas for the Jagad platform.
 *
 * These schemas define the shape of data exchanged between services.
 * They are the single source of truth for input/output validation
 * across the monorepo: server, scanner, and any future clients.
 */
import { z } from "zod";

// ─── Health ──────────────────────────────────────────────────────

export const HealthResponseSchema = z.object({
  status: z.enum(["ok", "degraded"]),
  timestamp: z.string(),
  version: z.string().optional(),
});

// ─── Quote ───────────────────────────────────────────────────────

// NOTE:
// Open quote routes are still backed by legacy Express handlers that perform
// their own validation and return legacy error payloads. The shared schema must
// therefore preserve raw transport input instead of rejecting/stripping it
// before the handler sees it.
export const GetQuoteInputSchema = z.object({
  from_asset: z.string().optional(),
  to_asset: z.string().optional(),
  from_amount: z.string().optional(),
  to_amount: z.string().optional(),
});

export const QuoteResponseSchema = z.object({
  uuid: z.string(),
  from_asset: z.string(),
  to_asset: z.string(),
  ex_rate: z.string(),
  inverse_rate: z.string(),
  from_amount: z.string().optional(),
  to_amount: z.string().optional(),
  total: z.string(),
});

// ─── Supported Currencies ────────────────────────────────────────

export const CurrencySchema = z.object({
  id: z.string(),
  uiDecimals: z.number(),
  assetGroup: z.string(),
  priceTicker: z.string(),
  network_fee: z.any().optional(),
  network: z.string().optional(),
  iconUrl: z.string().optional(),
});

// getSupportedCurrenciesFormatted() returns a plain array, not wrapped
export const GetSupportedCurrenciesResponseSchema = z.array(CurrencySchema);

// ─── Supported Banks / E-wallets ────────────────────────────────

const CutOffTimeSchema = z
  .object({
    start: z.string(),
    end: z.string(),
  })
  .passthrough();

export const SupportedBankSchema = z
  .object({
    id: z.string(),
    code: z.string(),
    code_number: z.string(),
    name: z.string(),
    display_name: z.string(),
    min_amount: z.number(),
    max_amount: z.number(),
    icon_url: z.string(),
    cut_off_time: CutOffTimeSchema.optional(),
  })
  .passthrough();

export const SupportedBankListResponseSchema = z.array(SupportedBankSchema);

export const SupportedEwalletSchema = z
  .object({
    provider: z.string(),
    name: z.string(),
    code: z.string(),
    iconUrl: z.string(),
    max_amount: z.number(),
    min_amount: z.number(),
  })
  .passthrough();

export const SupportedEwalletListResponseSchema = z.array(
  SupportedEwalletSchema
);

// ─── Bank Account ───────────────────────────────────────────────

export const GetBankAccountInputSchema = z.object({
  uuid: z.string().min(1, "Bank uuid is empty"),
});

export const AddBankAccountInputSchema = z.object({
  asset: z.string().optional().default("idr"),
  account_nickname: z.string().optional(),
  bank_name: z.string().optional(),
  bank_account: z.string().optional(),
  source: z.string().optional().default("REDEMPTION"),
});

export const UpdateBankAccountInputSchema = z.object({
  uuid: z.string().min(1, "uuid is not defined. Please define key."),
  account_nickname: z.string().optional(),
  bank_name: z.string().min(1, "Bank Name is not defined. Please define key."),
  bank_account: z
    .string()
    .min(1, "Bank Account is not defined. Please define key."),
});

export const DeleteBankAccountInputSchema = z.object({
  uuid: z.string().min(1, "Bank uuid is empty"),
});

export const BankAccountResponseSchema = z
  .object({
    account_nickname: z.string().nullable().optional(),
    asset: z.string(),
    bank_account: z.string(),
    bank_account_name: z.string().nullable().optional(),
    bank_name: z.string(),
    bank_uuid: z.string(),
    created_at: z.string(),
    is_deleted: z.boolean(),
    is_favorite: z.boolean(),
    public_address: z.string(),
    source: z.string(),
    status: z.string(),
    updated_at: z.string(),
    validated_at: z.string().nullable().optional(),
  })
  .passthrough();

export const BankAccountListResponseSchema = z.array(BankAccountResponseSchema);

// ─── E-wallet Account ───────────────────────────────────────────

export const GetEwalletAccountInputSchema = z.object({
  uuid: z.string().min(1, "Ewallet uuid is empty"),
});

export const AddEwalletAccountInputSchema = z.object({
  provider: z
    .string()
    .min(1, "Ewallet provider is not defined. Please define key."),
  phone_number: z.string().min(8, "Phone number must be at least 8 characters"),
  label: z.string().min(1, "Label is not defined. Please define key."),
});

export const UpdateEwalletAccountInputSchema = z.object({
  uuid: z.string().min(1, "Ewallet uuid is empty"),
  provider: z
    .string()
    .min(1, "Ewallet provider is not defined. Please define key."),
  phone_number: z.string().min(8, "Phone number must be at least 8 characters"),
  label: z.string().min(1, "Label is not defined. Please define key."),
});

export const DeleteEwalletAccountInputSchema = z.object({
  uuid: z.string().min(1, "Ewallet uuid is empty"),
});

export const EwalletAccountResponseSchema = z
  .object({
    uuid: z.string(),
    owner: z.string(),
    provider: z.string(),
    phone_number: z.string(),
    label: z.string(),
    is_deleted: z.boolean(),
    is_favorite: z.boolean(),
    created_at: z.string(),
    updated_at: z.string(),
    status: z.string(),
    validated_at: z.string().nullable().optional(),
    iconUrl: z.string().optional(),
  })
  .passthrough();

export const EwalletAccountListResponseSchema = z.array(
  EwalletAccountResponseSchema
);

// ─── Common / Shared ─────────────────────────────────────────────

export const PaginationInputSchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).optional(),
  cursor: z.coerce.number().int().min(0).default(0),
});

export const ErrorResponseSchema = z.object({
  code: z.string(),
  message: z.string(),
  data: z.unknown().optional(),
});

export const NoContentResponseSchema = z.void();

export const LegacyInvalidStatusResponseSchema = z.object({
  status: z.literal("INVALID"),
});

// ─── Admin: Auth ────────────────────────────────────────────────

export const AdminLoginInputSchema = z.object({
  email: z.string().min(1, "Email is required"),
  password: z.string().min(1, "Password is required"),
  otp_token: z.string().min(1, "OTP token is required"),
});

export const AdminRefreshInputSchema = z.object({
  refresh_token: z.string().min(1, "Refresh token is required"),
});

// ─── Admin: Common param schemas ────────────────────────────────

export const AdminUuidParamSchema = z.object({
  uuid: z.string().min(1, "UUID is required"),
});

export const AdminIdParamSchema = z.object({
  id: z.coerce.number().int("ID must be an integer"),
});

// ─── Admin: Top-up ──────────────────────────────────────────────

export const AdminRejectTopUpInputSchema = z.object({
  uuid: z.string().min(1, "UUID is required"),
  rejection_reason: z.string().optional(),
  rejection_detail: z.string().optional(),
  return_tx_id: z.string().optional(),
});

// ─── Admin: Redeem Bank ─────────────────────────────────────────

export const AdminApproveRedeemBankInputSchema = z.object({
  uuid: z.string().min(1, "UUID is required"),
  source_bank: z.string().optional(),
  source_account: z.string().optional(),
  transfer_date: z.string().optional(),
  transaction_reference: z.string().optional(),
});

export const AdminRejectRedeemInputSchema = z.object({
  uuid: z.string().min(1, "UUID is required"),
  rejection_reason: z.string().optional(),
  rejection_detail: z.string().optional(),
  return_tx_id: z.string().optional(),
});

// ─── Admin: Freeze ──────────────────────────────────────────────

export const AdminSubmitFreezeInputSchema = z.object({
  action: z.enum(["FREEZE", "UNFREEZE"]),
  target_address: z.string().min(1, "target_address is required"),
});

// ─── Admin: Mint ────────────────────────────────────────────────

export const AdminMintRequestInputSchema = z.object({
  action: z.literal("MINT"),
  target_address: z.string().min(1, "target_address is required"),
  mint_amount: z.union([z.number(), z.string()]),
  user_label: z.string().optional(),
});

// ─── Admin: Merchant ────────────────────────────────────────────

export const AdminAddMerchantInputSchema = z.object({
  merchant_name: z.string().min(1),
  merchant_address: z.string().min(1),
  join_date: z.any(),
  pic_name: z.string().min(1),
  pic_phone: z.string().min(1),
  email: z.string().min(1),
  public_address: z.string().min(1),
});

export const AdminEditMerchantInputSchema = z.object({
  uuid: z.string().min(1, "UUID is required"),
  public_address: z.string().optional(),
  merchant_name: z.string().optional(),
  merchant_address: z.string().optional(),
  join_date: z.any().optional(),
  pic_name: z.string().optional(),
  pic_phone: z.string().optional(),
  email: z.string().optional(),
});

// ─── Admin: Currency ────────────────────────────────────────────

export const AdminAddCurrencyInputSchema = z.object({
  asset_id: z.string().min(1, "asset_id is required"),
  is_top_up_blocked: z.boolean().optional().default(false),
  is_redeem_blocked: z.boolean().optional().default(false),
});

export const AdminEditCurrencyInputSchema = z.object({
  id: z.coerce.number().int(),
  asset_id: z.string().min(1, "asset_id is required"),
});

export const AdminToggleCurrencyFlagInputSchema = z.object({
  asset_id: z.string().min(1, "asset_id is required"),
  is_top_up_blocked: z.boolean(),
  is_redeem_blocked: z.boolean(),
});

// ─── Admin: ckBTC ───────────────────────────────────────────────

export const AdminChangeCkBtcStatusInputSchema = z.object({
  uuid: z.string().min(1, "UUID is required"),
  status: z.string().min(1, "status is required"),
});

// ─── Admin: Fireblocks ──────────────────────────────────────────

export const AdminHideVaultInputSchema = z.object({
  vaultId: z.string().min(1, "vaultId is required"),
});

export const AdminResendFireblocksWebhookByIDInputSchema = z.object({
  fireblocksTransactionId: z.string().min(1),
  resendCreated: z.boolean().optional(),
  resendStatusUpdated: z.boolean().optional(),
});

// ─── Admin: API User / Key ──────────────────────────────────────

export const AdminCreateProgrammaticUserInputSchema = z.object({
  company_name: z.string().min(1),
  email: z.string().min(1),
  public_address: z.string().min(1),
});

export const AdminCreateApiKeyInputSchema = z.object({
  id: z.string().min(1, "User ID is required"),
});

export const AdminDeactivateApiKeyInputSchema = z.object({
  id: z.string().min(1, "User ID is required"),
  apikey: z.string().min(1, "API key is required"),
});

// ─── Admin: User Vault ──────────────────────────────────────────

export const AdminSetUserVaultIntervalInputSchema = z.object({
  interval: z.number(),
});

// ─── Admin: Settlement ──────────────────────────────────────────

export const AdminSettlementSummarizeInputSchema = z.object({
  status: z.string().optional(),
  list_uuid: z.array(z.string()).optional(),
});

export const AdminUpdateSettlementCTFInputSchema = z.object({
  new_status: z.string().min(1, "new_status is required"),
  list_uuid: z.array(z.string()),
  return_proof_url: z.string().optional(),
  deposit_returned_date: z.any().optional(),
  redeem_request_uuid: z.string().optional(),
  disbursement_sent_date: z.any().optional(),
});

export const AdminUpdateSettlementFTCInputSchema = z.object({
  new_status: z.string().min(1, "new_status is required"),
  list_uuid: z.array(z.string()),
  disbursement_sent_date: z.any().optional(),
  disbursement_address: z.string().optional(),
  disbursement_proof_url: z.string().optional(),
});

export const AdminResendSettlementWebhooksInputSchema = z.object({
  settlementUuid: z.string().min(1, "settlementUuid is required"),
  settlementType: z.enum([
    "SETTLEMENT_CRYPTO_TO_FIAT",
    "SETTLEMENT_FIAT_TO_CRYPTO",
  ]),
  webhookEvent: z.string().optional(),
});

// ─── Admin: Payment Link ────────────────────────────────────────

export const AdminReturnDepositInputSchema = z.object({
  uuid: z.string().min(1, "UUID is required"),
  return_proof_url: z.string().min(1, "return_proof_url is required"),
  payment_returned_date: z.any(),
});

export const AdminPaymentLinkDetailInputSchema = z.object({
  payment_uuid: z.string().min(1, "payment_uuid is required"),
});

// ─── Admin: Transfer KYC ────────────────────────────────────────

export const AdminTransferKycInputSchema = z.object({
  solana_address: z.string().min(1, "Solana address is required"),
  ton_address: z.string().min(1, "TON address is required"),
});

// ─── Admin: DP Balance ──────────────────────────────────────────

export const AdminGetDPBalanceInputSchema = z.object({
  version: z.string().optional().default("v2"),
});

// ─── Admin: Blacklisted Destination ─────────────────────────────

export const AdminAddBlacklistedDestinationInputSchema = z.object({
  account_type: z.enum(["BANK", "EWALLET"]),
  bank_code: z.string().optional(),
  bank_account: z.string().optional(),
  phone_number: z.string().optional(),
  ewallet_provider: z.string().optional(),
  reason: z.string().min(1, "reason is required"),
});

export const AdminDeleteBlacklistedDestinationInputSchema = z.object({
  uuid: z.string().min(1, "UUID is required"),
  reason: z.string().min(1, "reason is required"),
});

// ─── Admin: Transfer P2P ────────────────────────────────────────

export const AdminTransferP2PDetailInputSchema = z.object({
  uuid: z.string().min(1, "UUID is required"),
});

// ─── Admin: Captcha Requirements ────────────────────────────────

export const AdminAddCaptchaRequirementInputSchema = z.object({
  public_address: z.string().min(1, "Public address is required"),
  operation_type: z.string().min(1, "Operation type is required"),
  is_required: z.boolean(),
  reason: z.string().optional().nullable(),
});

export const AdminUpdateCaptchaRequirementInputSchema = z.object({
  id: z.coerce.number().int(),
  public_address: z.string().optional(),
  operation_type: z.string().optional(),
  is_required: z.boolean().optional(),
  reason: z.string().optional().nullable(),
});

export const AdminDetailCaptchaRequirementInputSchema = z.object({
  id: z.coerce.number().int(),
});

// ─── Admin: Announcements ───────────────────────────────────────

export const AdminCreateAnnouncementInputSchema = z.object({
  announcement_text: z.string().min(1, "announcement_text is required"),
  primary_color: z.string().min(1, "primary_color is required"),
  secondary_color: z.string().min(1, "secondary_color is required"),
  button_text: z.string().optional(),
  button_destination: z.string().optional(),
  redirection_type: z.string().optional(),
  platform: z.string().optional().default("ALL"),
  is_active: z.boolean().optional().default(true),
  priority: z.number().optional().default(0),
});

export const AdminUpdateAnnouncementInputSchema = z.object({
  uuid: z.string().min(1, "UUID is required"),
  announcement_text: z.string().optional(),
  primary_color: z.string().optional(),
  secondary_color: z.string().optional(),
  button_text: z.string().optional(),
  button_destination: z.string().optional(),
  redirection_type: z.string().optional(),
  platform: z.string().optional(),
  is_active: z.boolean().optional(),
  priority: z.number().optional(),
});

// ─── Admin: Permission Templates ────────────────────────────────

export const AdminPermissionRestrictionsSchema = z.object({
  banking_redemption_disabled: z.boolean(),
  ewallet_redemption_disabled: z.boolean(),
  banking_crud_disabled: z.boolean(),
  ewallet_crud_disabled: z.boolean(),
  p2p_transfer_disabled: z.boolean(),
  payment_disabled: z.boolean(),
  private_key_export_disabled: z.boolean(),
  ppob_disabled: z.boolean(),
});

export const AdminPermissionTemplateInputSchema =
  AdminPermissionRestrictionsSchema.extend({
    description: z.string().optional().nullable(),
  });

export const AdminUpdatePermissionTemplateInputSchema = z.union([
  z.object({
    uuid: z.string().min(1, "UUID is required"),
    description: z.string().optional().nullable(),
  }),
  AdminPermissionRestrictionsSchema.extend({
    uuid: z.string().min(1, "UUID is required"),
    description: z.string().optional().nullable(),
  }),
]);

// ─── Admin: User Restrictions ───────────────────────────────────

export const AdminUserRestrictionsInputSchema = z.object({
  publicAddress: z.string().min(1, "publicAddress is required"),
  banking_redemption_disabled: z.boolean().optional(),
  ewallet_redemption_disabled: z.boolean().optional(),
  banking_crud_disabled: z.boolean().optional(),
  ewallet_crud_disabled: z.boolean().optional(),
  p2p_transfer_disabled: z.boolean().optional(),
  payment_disabled: z.boolean().optional(),
  private_key_export_disabled: z.boolean().optional(),
  ppob_disabled: z.boolean().optional(),
  reason: z.string().min(1, "reason is required"),
});

export const AdminAssignPermissionListInputSchema = z.object({
  publicAddress: z.string().min(1, "publicAddress is required"),
  permission_list_uuid: z.string().min(1, "permission_list_uuid is required"),
});

export const AdminPublicAddressParamSchema = z.object({
  publicAddress: z.string().min(1, "publicAddress is required"),
});

// ─── Admin: E-Wallet Management ─────────────────────────────────

export const AdminAddEWalletInputSchema = z.object({
  publicAddress: z.string().min(1, "publicAddress is required"),
  provider: z.string().min(1, "provider is required"),
  phone_number: z.string().min(8, "Phone number must be at least 8 characters"),
  label: z.string().min(1, "Label is required").max(255),
});

export const AdminDeleteEWalletInputSchema = z.object({
  publicAddress: z.string().min(1, "publicAddress is required"),
  ewalletUuid: z.string().min(1, "ewalletUuid is required"),
});

// ─── Admin: Escrow ──────────────────────────────────────────────

export const AdminEscrowUuidInputSchema = z.object({
  uuid: z.string().min(1, "UUID is required"),
});

// ─── Admin: List Query Params (reusable) ───────────────────────

export const AdminListQuerySchema = z.object({
  size: z.coerce.number().int().optional(),
  page: z.coerce.number().int().optional(),
  sort_by: z.string().optional(),
  status: z.string().optional(),
  platform: z.string().optional(),
  public_address: z.string().optional(),
  uuid: z.string().optional(),
  start_date: z.string().optional(),
  end_date: z.string().optional(),
  phone_number: z.string().optional(),
  bank_account: z.string().optional(),
  search: z.string().optional(),
  category: z.string().optional(),
  action_type: z.string().optional(),
  ton_address: z.string().optional(),
  sumsub_id: z.string().optional(),
  sortBy: z.string().optional(),
  sortDirection: z.string().optional(),
  operation_type: z.enum(["REDEMPTION", "PAYMENT"]).optional(),
});

export const AdminListPaginationSchema = z
  .object({
    page: z.number(),
    size: z.number(),
    total: z.number(),
    total_pages: z.number(),
  })
  .passthrough();

// ─── Admin: PPOB ────────────────────────────────────────────────

export const AdminPpobProductIdInputSchema = z.object({
  id: z.coerce.number().int(),
});

export const AdminPpobTransactionUuidInputSchema = z.object({
  uuid: z.string().min(1, "UUID is required"),
});

export const AdminPpobProductUpdateInputSchema = z.object({
  id: z.coerce.number().int(),
  sellingPrice: z.number().optional(),
  platformFee: z.number().optional(),
  productAmount: z.string().optional(),
  buyerProductStatus: z.boolean().optional(),
  sellerProductStatus: z.boolean().optional(),
  stock: z.number().optional(),
  desc: z.string().optional(),
  productNameEn: z.string().optional(),
  descEn: z.string().optional(),
  isTestProduct: z.boolean().optional(),
});

// ─── Admin: Generic output (DB-dependent shapes) ────────────────

export const AdminGenericResponseSchema = z.any();

const AdminDateTimeStringSchema = z.string().nullable().optional();

export const AdminActionResponseSchema = z
  .object({
    success: z.boolean().optional(),
    message: z.string().optional(),
    error: z.string().optional(),
    status: z.union([z.string(), z.number()]).optional(),
  })
  .passthrough();

export const AdminNoContentResponseSchema = z.void();

const AdminReviewActionSchema = z
  .object({
    uuid: z.string(),
    maker: z.string().nullable().optional(),
    action_type: z.string(),
    decision: z.string().nullable().optional(),
    decision_maker: z.string().nullable().optional(),
    decided_at: AdminDateTimeStringSchema,
    status: z.string(),
  })
  .passthrough();

export const AdminFreezeListRowSchema = z
  .object({
    uuid: z.string(),
    target_address: z.string().nullable().optional(),
    ton_address: z.string().nullable().optional(),
    date_requested: z.string(),
    maker: z.string(),
    status: z.string(),
    tx_status: z.string().nullable().optional(),
    action_type: z.string(),
    created_at: z.string(),
  })
  .passthrough();

export const AdminFreezeListResponseSchema = z
  .object({
    page: z.number(),
    last_page: z.number(),
    total_hits: z.number(),
    result: z.array(AdminFreezeListRowSchema),
  })
  .passthrough();

export const AdminFreezeDetailSchema = z
  .object({
    uuid: z.string(),
    target_address: z.string().nullable().optional(),
    tx_hash: z.string().nullable().optional(),
    tx_status: z.string().nullable().optional(),
    recent_blockhash: z.string().nullable().optional(),
    failure_reason: z.string().nullable().optional(),
    error_description: z.string().nullable().optional(),
    created_at: z.string(),
    admin_action: AdminReviewActionSchema,
  })
  .passthrough();

export const AdminMintListRowSchema = z
  .object({
    uuid: z.string(),
    target_address: z.string().nullable().optional(),
    ton_address: z.string().nullable().optional(),
    date_requested: z.string(),
    maker: z.string(),
    status: z.string(),
    amount: z.union([z.number(), z.string()]),
  })
  .passthrough();

export const AdminMintListResponseSchema = z
  .object({
    page: z.number(),
    last_page: z.number(),
    total_hits: z.number(),
    result: z.array(AdminMintListRowSchema),
  })
  .passthrough();

export const AdminMintAddressSuggestionSchema = z
  .object({
    user_label: z.string().nullable().optional(),
    target_address: z.string(),
  })
  .passthrough();

export const AdminMintAddressSuggestionListResponseSchema = z.array(
  AdminMintAddressSuggestionSchema
);

export const AdminMintDetailSchema = z
  .object({
    uuid: z.string(),
    target_address: z.string().nullable().optional(),
    tx_hash: z.string().nullable().optional(),
    tx_status: z.string().nullable().optional(),
    recent_blockhash: z.string().nullable().optional(),
    failure_reason: z.string().nullable().optional(),
    error_description: z.string().nullable().optional(),
    created_at: z.string(),
    mint_action: z
      .object({
        uuid: z.string(),
        target_address: z.string().nullable().optional(),
        mint_amount: z.union([z.number(), z.string()]),
        mint_status: z.string().nullable().optional(),
        user_label: z.string().nullable().optional(),
      })
      .passthrough(),
    admin_action: AdminReviewActionSchema,
  })
  .passthrough();

export const AdminPermissionTemplateSchema = z
  .object({
    uuid: z.string(),
    restrictions: AdminPermissionRestrictionsSchema,
    description: z.string().nullable(),
    created_at: z.string(),
    updated_at: z.string().optional(),
    created_by: z.string().nullable().optional(),
    updated_by: z.string().nullable().optional(),
    assigned_users_count: z.number().optional(),
  })
  .passthrough();

export const AdminPermissionTemplateListResponseSchema = z
  .object({
    templates: z.array(AdminPermissionTemplateSchema),
  })
  .passthrough();

export const AdminPermissionTemplateMutationResponseSchema =
  AdminPermissionTemplateSchema;

export const AdminUserRestrictionsResponseSchema = z
  .object({
    user_uuid: z.string(),
    restrictions: AdminPermissionRestrictionsSchema.partial(),
  })
  .passthrough();

export const AdminAssignPermissionListResponseSchema = z
  .object({
    user_uuid: z.string(),
    permission_list_uuid: z.string(),
    restrictions: AdminPermissionRestrictionsSchema,
    updated_at: z.string(),
    updated_by: z.string(),
  })
  .passthrough();

export const AdminDeleteUserRestrictionsResponseSchema = z
  .object({
    user_uuid: z.string(),
    message: z.string(),
  })
  .passthrough();

export const AdminAnnouncementSchema = z
  .object({
    uuid: z.string(),
    announcement_text: z.string(),
    primary_color: z.string(),
    secondary_color: z.string(),
    button_text: z.string().nullable().optional(),
    button_destination: z.string().nullable().optional(),
    redirection_type: z.string().nullable().optional(),
    platform: z.string(),
    is_active: z.boolean(),
    priority: z.number(),
    created_at: z.string().optional(),
    updated_at: z.string().optional(),
  })
  .passthrough();

export const AdminAnnouncementListResponseSchema = z
  .object({
    success: z.boolean().optional(),
    data: z.array(AdminAnnouncementSchema),
    pagination: AdminListPaginationSchema.optional(),
  })
  .passthrough();

export const AdminAnnouncementMutationResponseSchema = z
  .object({
    success: z.boolean(),
    message: z.string().optional(),
    data: AdminAnnouncementSchema.optional(),
  })
  .passthrough();

export const AdminCaptchaRequirementSchema = z
  .object({
    id: z.number(),
    public_key: z.string(),
    operation_type: z.string(),
    is_required: z.boolean(),
    reason: z.string().nullable().optional(),
    admin_uuid: z.string().nullable().optional(),
    is_deleted: z.boolean().optional(),
    created_at: z.string(),
    updated_at: z.string().optional(),
    user_profile: z
      .object({
        public_address: z.string().optional(),
        user_uuid: z.string().optional(),
      })
      .partial()
      .optional(),
    admin_profile: z
      .object({
        email_address: z.string().optional(),
      })
      .partial()
      .optional(),
  })
  .passthrough();

export const AdminCaptchaRequirementMutationDataSchema = z
  .object({
    id: z.number(),
    public_address: z.string(),
    operation_type: z.string(),
    is_required: z.boolean(),
    reason: z.string().nullable().optional(),
    admin_uuid: z.string().nullable().optional(),
    created_at: z.string(),
    updated_at: z.string().optional(),
  })
  .passthrough();

export const AdminCaptchaRequirementListResponseSchema = z.union([
  z
    .object({
      success: z.literal(true),
      data: z.array(AdminCaptchaRequirementSchema),
      total_hits: z.number(),
      page: z.number(),
      last_page: z.number(),
    })
    .passthrough(),
  z
    .object({
      success: z.literal(false),
      error: z.string(),
      details: z.unknown().optional(),
    })
    .passthrough(),
]);

export const AdminCaptchaRequirementDetailResponseSchema = z.union([
  z
    .object({
      success: z.literal(true),
      data: AdminCaptchaRequirementSchema,
    })
    .passthrough(),
  z
    .object({
      success: z.literal(false),
      error: z.string(),
      details: z.unknown().optional(),
    })
    .passthrough(),
]);

export const AdminCaptchaRequirementMutationResponseSchema = z.union([
  z
    .object({
      success: z.literal(true),
      data: AdminCaptchaRequirementMutationDataSchema,
    })
    .passthrough(),
  z
    .object({
      success: z.literal(false),
      error: z.string(),
      details: z.unknown().optional(),
      existing_requirement_id: z.number().optional(),
    })
    .passthrough(),
]);

export const AdminEwalletAccountSchema = z
  .object({
    uuid: z.string(),
    owner: z.string(),
    provider: z.string(),
    phone_number: z.string(),
    label: z.string(),
    status: z.string(),
    validated_at: AdminDateTimeStringSchema,
    is_deleted: z.boolean(),
    created_at: z.string(),
    updated_at: z.string().optional(),
  })
  .passthrough();

export const AdminEwalletAccountListResponseSchema = z
  .object({
    data: z.array(AdminEwalletAccountSchema),
    total: z.number(),
    page: z.number(),
    limit: z.number(),
  })
  .passthrough();

export const AdminBankingAccountSchema = z
  .object({
    bank_uuid: z.string(),
    public_address: z.string(),
    bank_name: z.string(),
    bank_account: z.string(),
    bank_account_name: z.string().nullable().optional(),
    account_nickname: z.string(),
    asset: z.string(),
    source: z.string(),
    status: z.string(),
    validated_at: AdminDateTimeStringSchema,
    is_deleted: z.boolean(),
    created_at: z.string(),
    updated_at: z.string().optional(),
  })
  .passthrough();

export const AdminBankingAccountListResponseSchema = z
  .object({
    data: z.array(AdminBankingAccountSchema),
    total: z.number(),
    page: z.number(),
    limit: z.number(),
  })
  .passthrough();

export const AdminSpreadSchema = z
  .object({
    uuid: z.string(),
    source_asset_id: z.string(),
    destination_asset_id: z.string(),
    spread_in_bps: z.number(),
  })
  .passthrough();

export const AdminSpreadListResponseSchema = z
  .object({
    status: z.string(),
    data: z.array(AdminSpreadSchema),
    total_hits: z.number(),
    page: z.number(),
    last_page: z.number(),
  })
  .passthrough();

export const AdminUserLimitSchema = z
  .object({
    id: z.number(),
    public_address: z.string(),
    ton_address: z.string().nullable().optional(),
    interval: z.string().optional(),
    redeem_request: z.union([z.number(), z.string()]),
    redeem_to_crypto: z.union([z.number(), z.string()]),
    created_at: z.string(),
    updated_at: z.string(),
  })
  .passthrough();

export const AdminUserLimitListResponseSchema = z
  .object({
    page: z.number(),
    last_page: z.number(),
    total_hits: z.number(),
    result: z.array(AdminUserLimitSchema),
  })
  .passthrough();

export const AdminSystemStatusSchema = z
  .object({
    subsystem: z.string(),
    status: z.string(),
    warning_text: z.string().nullable().optional(),
    manual_override: z.boolean(),
    updated_at: z.string(),
  })
  .passthrough();

export const AdminSystemStatusListResponseSchema = z.array(
  AdminSystemStatusSchema
);

export const AdminMerchantSchema = z
  .object({
    uuid: z.string(),
    business_name: z.string(),
    address: z.string().nullable().optional(),
    join_date: AdminDateTimeStringSchema,
    pic_name: z.string().nullable().optional(),
    pic_phone: z.string().nullable().optional(),
    email: z.string().nullable().optional(),
    public_address: z.string(),
    created_at: z.string().optional(),
    updated_at: z.string().optional(),
    is_deleted: z.boolean().optional(),
  })
  .passthrough();

export const AdminMerchantListResponseSchema = z
  .object({
    data: z.array(AdminMerchantSchema),
    pagination: AdminListPaginationSchema,
  })
  .passthrough();

export const AdminCurrencySchema = z
  .object({
    id: z.number(),
    asset_id: z.string(),
    is_top_up_blocked: z.boolean(),
    is_redeem_blocked: z.boolean(),
    is_deleted: z.boolean().optional(),
    created_at: z.string().optional(),
    updated_at: z.string().optional(),
  })
  .passthrough();

export const AdminCurrencyListResponseSchema = z.array(AdminCurrencySchema);

export const AdminTonPrefunderSchema = z
  .object({
    id: z.number(),
    pubkey: z.string(),
    address: z.string(),
    balance: z.number(),
  })
  .passthrough();

export const AdminTonPrefunderListResponseSchema = z.array(
  AdminTonPrefunderSchema
);

export const AdminBlacklistedDestinationSchema = z
  .object({
    uuid: z.string(),
    account_type: z.string(),
    bank_code: z.string().nullable().optional(),
    bank_account: z.string().nullable().optional(),
    phone_number: z.string().nullable().optional(),
    ewallet_provider: z.string().nullable().optional(),
    reason: z.string().nullable().optional(),
    is_blacklisted: z.boolean(),
    created_at: z.string().optional(),
    updated_at: z.string().optional(),
    admin_uuid: z.string().nullable().optional(),
    admin_profile: z
      .object({
        email_address: z.string().optional(),
        admin_uuid: z.string().optional(),
      })
      .partial()
      .optional(),
  })
  .passthrough();

export const AdminBlacklistedDestinationListResponseSchema = z
  .object({
    page: z.number(),
    last_page: z.number(),
    total_hits: z.number(),
    result: z.array(AdminBlacklistedDestinationSchema),
  })
  .passthrough();

export const AdminKycUserSchema = z
  .object({
    user_uuid: z.string().nullable().optional(),
    sumsub_id: z.string().nullable().optional(),
    solana_address: z.string().nullable().optional(),
    ton_addresses: z.array(z.string()),
    kyc_completed_at: z.string().nullable().optional(),
    created_at: z.string().nullable().optional(),
    updated_at: z.string().nullable().optional(),
  })
  .passthrough();

export const AdminKycUserListResponseSchema = z
  .object({
    data: z.array(AdminKycUserSchema),
    pagination: z
      .object({
        page: z.number(),
        size: z.number(),
        total: z.number(),
        total_pages: z.number(),
      })
      .passthrough(),
  })
  .passthrough();

export const AdminSpamTopupSchema = z
  .object({
    activity_uuid: z.string(),
    transaction_uuid: z.string(),
    public_address: z.string(),
    asset_id: z.string(),
    asset_name: z.string(),
    crypto_amount: z.union([z.number(), z.string()]),
    idr_amount: z.number(),
    created_at: z.string(),
  })
  .passthrough();

export const AdminSpamTopupListResponseSchema = z
  .object({
    page: z.number(),
    last_page: z.number(),
    total_hits: z.number(),
    result: z.array(AdminSpamTopupSchema),
  })
  .passthrough();

export const AdminSpamTopupDeleteResponseSchema = z
  .object({
    soft_deleted_count: z.number(),
  })
  .passthrough();

export const AdminReferralProgramCodeSummarySchema = z
  .object({
    id: z.number(),
    uuid: z.string().optional(),
    code: z.string(),
    is_active: z.boolean(),
    referrer: z.string(),
    limit: z.number(),
    created_at: z.string(),
    updated_at: z.string().optional(),
    usage_count: z.number().optional(),
    _count: z
      .object({
        referral_activity: z.number(),
      })
      .optional(),
  })
  .passthrough();

export const AdminReferralProgramSummarySchema = z
  .object({
    id: z.number(),
    program_name: z.string(),
    description: z.string().nullable().optional(),
    minimum_top_up: z.union([z.number(), z.string()]),
    referrer_reward_amount: z.union([z.number(), z.string()]),
    referee_reward_amount: z.union([z.number(), z.string()]),
    program_type: z.string(),
    event_start_date: AdminDateTimeStringSchema,
    event_end_date: AdminDateTimeStringSchema,
    created_at: z.string(),
    updated_at: z.string().optional(),
    status: z.string(),
    statistics: z
      .object({
        total_codes: z.number(),
        active_codes: z.number(),
        total_usage: z.number(),
        total_rewards_disbursed: z.number().optional(),
      })
      .passthrough(),
    referral_codes: z.array(AdminReferralProgramCodeSummarySchema).optional(),
  })
  .passthrough();

export const AdminReferralProgramListResponseSchema = z
  .object({
    programs: z.array(AdminReferralProgramSummarySchema),
    total_hits: z.number(),
    page: z.number(),
    limit: z.number(),
  })
  .passthrough();

export const AdminReferralProgramDetailResponseSchema = z.union([
  z
    .object({
      success: z.literal(true),
      data: AdminReferralProgramSummarySchema,
    })
    .passthrough(),
  z
    .object({
      success: z.literal(false),
      message: z.string(),
    })
    .passthrough(),
]);

export const AdminReferralActivitySummarySchema = z
  .object({
    id: z.number().optional(),
    uuid: z.string(),
    referred_user: z.string().optional(),
    referred_user_address: z.string().optional(),
    status: z.string(),
    created_at: z.string(),
    updated_at: z.string().optional(),
    has_rewards: z.boolean().optional(),
    referral_user: z
      .object({
        id: z.number().optional(),
        public_address: z.string(),
      })
      .partial()
      .optional(),
    rewards: z
      .object({
        id: z.number(),
        referrer_reward: z.number(),
        referee_reward: z.number(),
        created_at: z.string().optional(),
        updated_at: z.string().optional(),
      })
      .nullable()
      .optional(),
  })
  .passthrough();

export const AdminReferralCodeSummarySchema = z
  .object({
    id: z.number(),
    uuid: z.string(),
    code: z.string(),
    referrer: z.string(),
    program: z.string(),
    limit: z.number(),
    is_active: z.boolean(),
    expiry_date: AdminDateTimeStringSchema,
    created_at: z.string(),
    updated_at: z.string().optional(),
    status: z.string(),
    usage_statistics: z
      .object({
        usage_count: z.number(),
        remaining_uses: z.number(),
        rewards_disbursed: z.number(),
      })
      .passthrough()
      .optional(),
    statistics: z
      .object({
        total_usage: z.number(),
        remaining_uses: z.number(),
        successful_referrals: z.number(),
        pending_referrals: z.number(),
        total_rewards_disbursed: z.number(),
        conversion_rate: z.number(),
      })
      .passthrough()
      .optional(),
    referral_program: z
      .object({
        id: z.number(),
        program_name: z.string(),
        description: z.string().nullable().optional(),
        program_type: z.string(),
        minimum_top_up: z.union([z.number(), z.string()]).optional(),
        referrer_reward_amount: z.union([z.number(), z.string()]).optional(),
        referee_reward_amount: z.union([z.number(), z.string()]).optional(),
        event_start_date: AdminDateTimeStringSchema,
        event_end_date: AdminDateTimeStringSchema,
        created_at: z.string().optional(),
        updated_at: z.string().optional(),
      })
      .passthrough()
      .nullable()
      .optional(),
    recent_activity: z.array(AdminReferralActivitySummarySchema).optional(),
    referral_activity: z.array(AdminReferralActivitySummarySchema).optional(),
  })
  .passthrough();

export const AdminReferralCodeListResponseSchema = z
  .object({
    codes: z.array(AdminReferralCodeSummarySchema),
    total_hits: z.number(),
    page: z.number(),
    limit: z.number(),
  })
  .passthrough();

export const AdminReferralCodeDetailResponseSchema = z.union([
  z
    .object({
      success: z.literal(true),
      result: AdminReferralCodeSummarySchema,
    })
    .passthrough(),
  z
    .object({
      success: z.literal(false),
      message: z.string(),
    })
    .passthrough(),
]);

// ─── Generic response (reused across wallet, walletV2, etc.) ────

export const GenericResponseSchema = z.any();

// ─── Wallet V1: Auth / Register / Recover ───────────────────────

export const WalletAuthInputSchema = z.object({
  public_address: z.string().min(1, "public_address is required"),
  signature: z.string().min(1, "signature is required"),
  network: z.string().optional(),
});

export const WalletRegisterInputSchema = z.object({
  public_address: z.string().min(1, "public_address is required"),
  network: z.string().optional(),
  referral_code: z.string().optional(),
});

export const WalletRecoverAccountInputSchema = z.object({
  public_address: z.string().min(1, "public_address is required"),
  signature: z.string().min(1, "signature is required"),
  network: z.string().optional(),
});

// ─── Wallet V1: Transactions ────────────────────────────────────

export const WalletTransactionListInputSchema = z.object({
  page: z.coerce.number().int().optional(),
  limit: z.coerce.number().int().optional(),
  type: z.string().optional(),
  status: z.string().optional(),
});

export const WalletTransactionDetailInputSchema = z.object({
  uuid: z.string().min(1, "UUID is required"),
});

// ─── Wallet V1: Exchange Rate ───────────────────────────────────

export const WalletExchangeRateInputSchema = z
  .object({
    from_asset: z.string().min(1, "from_asset is required"),
    to_asset: z.string().min(1, "to_asset is required"),
    from_amount: z.coerce.string().min(1).optional(),
    to_amount: z.coerce.string().min(1).optional(),
  })
  .refine((v) => v.from_amount !== undefined || v.to_amount !== undefined, {
    message: "Provide from_amount or to_amount",
  });

export const WalletExchangeRateResponseSchema = z
  .object({
    uuid: z.string(),
    ex_rate: z.string(),
    inverse_rate: z.string(),
    size: z.string(),
    total: z.string(),
    fees: z.object({
      network_fee: z.number(),
      income_tax: z.number(),
      vat: z.number(),
      total_fees: z.number(),
      va_fee: z.number().optional(),
    }),
    valid_time: z.number(),
  })
  .passthrough();

// ─── Wallet V1: Deposit Address ─────────────────────────────────

export const WalletRequestDepositAddressInputSchema = z.object({
  asset: z.string().min(1, "asset is required"),
  network: z.string().optional(),
});

export const WalletRequestDepositAddressResponseSchema = z
  .object({
    address: z.string(),
  })
  .passthrough();

// ─── Wallet V1: Redeem ──────────────────────────────────────────

export const WalletRedeemToCryptoInputSchema = z.object({
  asset: z.string().min(1, "asset is required"),
  amount: z.string().min(1, "amount is required"),
  destination: z.string().min(1, "destination is required"),
  network: z.string().optional(),
});

// ─── Wallet V1: Banking ─────────────────────────────────────────

export const WalletBankingAddInputSchema = z.object({
  bank_name: z.string().min(1, "bank_name is required"),
  bank_account: z.string().min(1, "bank_account is required"),
  account_nickname: z.string().optional(),
  asset: z.string().optional(),
  source: z.string().optional(),
});

export const WalletBankingUuidInputSchema = z.object({
  bank_uuid: z.string().min(1, "bank_uuid is required"),
});

export const WalletBankingEditInputSchema = z.object({
  bank_uuid: z.string().min(1, "bank_uuid is required"),
  bank_name: z.string().optional(),
  bank_account: z.string().optional(),
  account_nickname: z.string().optional(),
});

// ─── Wallet V1: Whitelist Address ───────────────────────────────

export const WalletWhitelistAddInputSchema = z.object({
  address: z.string().min(1, "address is required"),
  label: z.string().optional(),
  network: z.string().optional(),
});

export const WalletWhitelistRemoveInputSchema = z.object({
  whitelist_uuid: z.string().min(1, "whitelist_uuid is required"),
});

// ─── Wallet V1: P2P Address ────────────────────────────────────

export const WalletP2PAddressInputSchema = z.object({
  name: z.string().min(1, "name is required"),
  address: z.string().min(1, "address is required"),
  address_tag: z.string().optional(),
  network: z.string().optional(),
  asset: z.string().optional(),
});

export const WalletP2PAddressUuidInputSchema = z.object({
  address_uuid: z.string().min(1, "address_uuid is required"),
});

export const WalletP2PAddressEditInputSchema = z.object({
  address_uuid: z.string().min(1, "address_uuid is required"),
  name: z.string().optional(),
  address: z.string().optional(),
  address_tag: z.string().optional(),
  network: z.string().optional(),
  asset: z.string().optional(),
});

// ─── Wallet V1: Pay / Transfer ──────────────────────────────────

export const WalletPayInputSchema = z.object({
  to: z.string().min(1, "to is required"),
  amount: z.string().min(1, "amount is required"),
  asset: z.string().optional(),
  memo: z.string().optional(),
});

export const WalletTransferInputSchema = z.object({
  to: z.string().min(1, "to is required"),
  amount: z.string().min(1, "amount is required"),
  asset: z.string().optional(),
  network: z.string().optional(),
  signature: z.string().optional(),
});

// ─── Wallet V1: Conversion ─────────────────────────────────────

export const WalletConversionInitiateInputSchema = z.object({
  from_asset: z.string().min(1, "from_asset is required"),
  to_asset: z.string().min(1, "to_asset is required"),
  amount: z.string().min(1, "amount is required"),
});

export const WalletConversionSendInputSchema = z.object({
  conversion_uuid: z.string().min(1, "conversion_uuid is required"),
  signature: z.string().min(1, "signature is required"),
});

// ─── Wallet V1: Merchant ───────────────────────────────────────

export const WalletMerchantAddInputSchema = z.object({
  business_name: z.string().min(1, "business_name is required"),
  email: z.string().email("email must be valid"),
});

export const WalletMerchantEditInputSchema = z.object({
  business_name: z.string().min(1, "business_name is required"),
  email: z.string().email("email must be valid"),
});

export const WalletMerchantResponseSchema = z
  .object({
    uuid: z.string(),
    business_name: z.string(),
    email: z.string(),
    image_url: z.string().nullable().optional(),
    public_address: z.string().optional(),
    created_at: z.string().optional(),
    updated_at: z.string().optional(),
  })
  .passthrough();

export const WalletMerchantUploadImageResponseSchema = z
  .object({
    uuid: z.string(),
    image_url: z.string().nullable().optional(),
  })
  .passthrough();

// ─── Wallet V1: Payment Link ───────────────────────────────────

export const WalletPaymentLinkCreateInputSchema = z.object({
  expected_amount: z.coerce.number(),
  asset: z.string().optional(),
  description: z.string().optional(),
  expiry_minutes: z.number().optional(),
});

export const WalletPaymentLinkDetailInputSchema = z.object({
  payment_uuid: z.string().min(1, "payment_uuid is required"),
});

export const WalletPaymentLinkVoidInputSchema = z.object({
  uuid: z.string().min(1, "uuid is required"),
});

// ─── Wallet V1: Escrow ─────────────────────────────────────────

export const WalletEscrowInitiateInputSchema = z.object({
  amount: z.string().min(1, "amount is required"),
  asset: z.string().optional(),
  network: z.string().optional(),
});

export const WalletEscrowSubmitDepositInputSchema = z.object({
  escrowUuid: z.string().min(1, "escrowUuid is required"),
  signedTx: z.string().min(1, "signedTx is required"),
});

// ─── Wallet V1: PPOB ───────────────────────────────────────────

export const WalletPpobPrepaidOrderInputSchema = z.object({
  buyer_sku_code: z.string().min(1, "buyer_sku_code is required"),
  customer_no: z.string().min(1, "customer_no is required"),
  testing: z.boolean().optional(),
  captcha_key: z.string().optional(),
  captcha_value: z
    .object({
      slide_x: z.number(),
      slide_y: z.number(),
    })
    .optional(),
});

export const WalletPpobPostpaidOrderInputSchema = z.object({
  ppob_inquiry_quote_uuid: z.string().optional(),
  buyer_sku_code: z.string().min(1, "buyer_sku_code is required"),
  customer_no: z.string().min(1, "customer_no is required"),
  captcha_key: z.string().optional(),
  captcha_value: z
    .object({
      slide_x: z.number(),
      slide_y: z.number(),
    })
    .optional(),
});

export const WalletPpobPostpaidInquiryInputSchema = z.object({
  buyer_sku_code: z.string().min(1, "buyer_sku_code is required"),
  customer_no: z.string().min(1, "customer_no is required"),
  testing: z.boolean().optional(),
});

export const WalletPpobOrderUuidInputSchema = z.object({
  uuid: z.string().min(1, "UUID is required"),
});

export const WalletPpobProductListInputSchema = z.object({
  category: z.string().optional(),
  brand: z.string().optional(),
  type: z.string().optional(),
  search: z.string().optional(),
  page: z.coerce.number().int().optional(),
  size: z.coerce.number().int().optional(),
  sortBy: z.string().optional(),
  sortDirection: z.enum(["asc", "desc"]).optional(),
});

// Recent destinations: the in-scope PpobProductCategory values the
// `/wallet/v1/ppob/recent-destinations` endpoint accepts (spec "in-scope
// service -> category"). Out-of-scope categories (INTERNET_BILL, EMONEY, ESIM)
// are rejected at validation, mirroring the handler's 400.
export const WalletPpobRecentDestinationCategorySchema = z.enum([
  "MOBILE_PREPAID",
  "MOBILE_DATA_PACKAGE",
  "MOBILE_POSTPAID",
  "ELECTRICITY_PREPAID",
  "ELECTRICITY_POSTPAID",
]);

export const WalletPpobRecentDestinationsInputSchema = z.object({
  category: WalletPpobRecentDestinationCategorySchema,
  limit: z.coerce.number().int().positive().default(5),
});

export const WalletPpobRecentDestinationSchema = z
  .object({
    customer_no: z.string(),
    display_no: z.string().nullable(),
    category: WalletPpobRecentDestinationCategorySchema,
    provider: z
      .object({
        id: z.string(),
        name: z.string(),
        image_url: z.string().nullable(),
      })
      .nullable(),
    last_used_at: z.string(),
  })
  .passthrough();

export const WalletPpobRecentDestinationsResponseSchema = z
  .object({
    data: z.array(WalletPpobRecentDestinationSchema),
  })
  .passthrough();

export const WalletPpobMobileProviderInputSchema = z.object({
  phone_number: z.string().min(8, "Phone number must be at least 8 characters"),
  service_category: z.string().min(1, "service_category is required"),
});

export const WalletPpobPlnInquiryInputSchema = z.object({
  customer_no: z.string().min(1, "customer_no is required"),
});

export const WalletPpobPlnInquiryResponseSchema = z
  .object({
    customer_no: z.string(),
    is_valid: z.boolean(),
    provider_rc: z.string(),
    provider_message: z.string(),
    customer: z
      .object({
        name: z.string(),
        segment_power: z.string(),
        meter_no: z.string(),
        subscriber_id: z.string(),
      })
      .nullable(),
  })
  .passthrough();

export const CaptchaChallengeDataSchema = z
  .object({
    id: z.string(),
    captcha_key: z.string(),
    // Image fields are absent in bypass-captcha mode (staging whitelisted IPs)
    master_image_base64: z.string().optional(),
    thumb_image_base64: z.string().optional(),
    master_width: z.number().optional(),
    master_height: z.number().optional(),
    thumb_width: z.number().optional(),
    thumb_height: z.number().optional(),
    display_x: z.number().optional(),
    display_y: z.number().optional(),
  })
  .passthrough();

export const CaptchaRequiredResponseSchema = z.union([
  z.object({
    captcha_required: z.literal(true),
    data: CaptchaChallengeDataSchema,
  }),
  CaptchaChallengeDataSchema.extend({
    captcha_required: z.literal(true).optional(),
  }),
]);

export const WalletPpobOrderEscrowResponseSchema = z
  .object({
    data: z.object({
      escrow: z.object({
        escrowUuid: z.string(),
        unsignedTx: z.string(),
        additionalData: z.object({
          seqNo: z.number(),
        }),
      }),
    }),
  })
  .passthrough();

export const WalletMigrationStatusSchema = z.enum([
  "MIGRATION_INITIATED",
  "JETTON_RETURN_INITIATED",
  "JETTON_RETURN_SUCCESS",
  "JETTON_RETURN_FAILED",
  "SC_MINT_INITIATED",
  "SC_MINT_FAILED",
  "COMPLETED_SC_MINT_SUCCESS",
  "COMPLETED_NO_JS",
]);

export const WalletMigrationStatusResponseSchema = z
  .object({
    user_id: z.string(),
    status: WalletMigrationStatusSchema,
  })
  .passthrough();

export const WalletMigrationCreateUnsignedJsResponseSchema = z.union([
  z
    .object({
      serializedTransaction: z.string(),
      seqNo: z.number(),
    })
    .passthrough(),
  z
    .object({
      status: WalletMigrationStatusSchema,
    })
    .passthrough(),
]);

export const WalletMigrationReturnJsMintScResponseSchema = z
  .object({
    migrationId: z.number(),
    status: WalletMigrationStatusSchema,
    estimatedSCAmount: z.string().nullable(),
    transactionHash: z.string(),
  })
  .passthrough();

// ─── Wallet V1: Asset Name / Config ────────────────────────────

export const WalletAssetNameInputSchema = z.object({
  asset: z.string().optional(),
  network: z.string().optional(),
});

// ─── Wallet V2: Redeem ─────────────────────────────────────────

export const WalletV2RedeemInitiateInputSchema = z.object({
  ui_amount: z.coerce.number().positive("ui_amount is required"),
  destination: z.enum(["bank", "e-wallet", "pay-bank"]),
  destination_uuid: z.string().min(1, "destination_uuid is required"),
  memo: z.string().optional(),
  captcha_key: z.string().optional(),
  captcha_value: z
    .object({
      slide_x: z.number(),
      slide_y: z.number(),
    })
    .optional(),
});

export const WalletV2RedeemBankSendInputSchema = z.object({
  uuid: z
    .string({ required_error: "uuid is required" })
    .min(1, "uuid is required"),
  data: z
    .string({ required_error: "data is required" })
    .min(1, "data is required"),
});

export const WalletV2RedeemEwalletSendInputSchema = z.object({
  uuid: z
    .string({ required_error: "uuid is required" })
    .min(1, "uuid is required"),
  data: z
    .string({ required_error: "data is required" })
    .min(1, "data is required"),
});

export const WalletV2RedeemInitiateResponseSchema = z.union([
  z
    .object({
      redeemRequestUuid: z.string(),
      seqNo: z.number().optional(),
      serializedTransaction: z.string(),
      captcha_required: z.literal(false).optional(),
    })
    .passthrough(),
  CaptchaRequiredResponseSchema,
]);

export const WalletV2RedeemSendResponseSchema = z
  .object({
    uuid: z.string(),
    status: z.string(),
    activity: z.object({
      uuid: z.string(),
    }),
    activity_uuid: z.string().optional(),
  })
  .passthrough();

// ─── Wallet V2: Activities ─────────────────────────────────────

export const WalletV2ActivityListInputSchema = z.object({
  page: z.coerce.number().int().optional(),
  limit: z.coerce.number().int().optional(),
  size: z.coerce.number().int().optional(),
  type: z.string().optional(),
  status: z.string().optional(),
  sort_by: z.string().optional(),
});

export const WalletV2ActivityDetailInputSchema = z.preprocess(
  (value) => {
    if (
      Array.isArray(value) &&
      value.every((item) => typeof item === "string")
    ) {
      return { activity_uuid: value.join("") };
    }

    if (value && typeof value === "object") {
      const numericEntries = Object.entries(value as Record<string, unknown>)
        .filter(([key]) => /^\d+$/.test(key))
        .sort(([left], [right]) => Number(left) - Number(right));

      if (
        numericEntries.length > 0 &&
        numericEntries.every(([, item]) => typeof item === "string")
      ) {
        return {
          activity_uuid: numericEntries.map(([, item]) => item).join(""),
        };
      }
    }

    return value;
  },
  z.object({
    activity_uuid: z.string().min(1, "activity_uuid is required"),
  })
);

export const WalletV2ActivitySummarySchema = z
  .object({
    uuid: z.string(),
    code: z.number(),
    name: z.string(),
    timestamp: z.number(),
    ui_amount: z.number(),
    unit: z.string(),
    status: z.string(),
    is_multipart: z.boolean(),
    amount: z.union([z.string(), z.number()]).optional(),
    fee: z.union([z.string(), z.number()]).optional(),
    ui_fee: z.number().optional(),
    transaction_uuid: z.string().optional(),
  })
  .passthrough();

export const WalletV2ActivityListResponseSchema = z.object({
  page: z.number(),
  last_page: z.number(),
  total_hits: z.number(),
  result: z.array(WalletV2ActivitySummarySchema),
});

// ─── Wallet V2: E-wallet Account ───────────────────────────────

export const WalletV2EwalletAddInputSchema = z.object({
  provider: z.string().min(1, "provider is required"),
  phone_number: z.string().min(8, "Phone number must be at least 8 characters"),
  label: z.string().min(1, "label is required"),
});

export const WalletV2EwalletUuidInputSchema = z.object({
  uuid: z.string().min(1, "UUID is required"),
});

export const WalletV2EwalletFavoriteInputSchema = z.object({
  uuid: z.string().min(1, "UUID is required"),
  is_favorite: z.boolean(),
});

export const WalletV2EwalletEditInputSchema = z.object({
  uuid: z.string().min(1, "UUID is required"),
  // Preserve legacy handler semantics for missing provider on edit.
  // The existing Express handler maps that case to a legacy error payload.
  provider: z.string().min(1, "provider is required").optional(),
  phone_number: z.string().min(8, "Phone number must be at least 8 characters"),
  label: z.string().min(1, "label is required"),
});

// ─── Wallet V2: Banking Account ────────────────────────────────

export const WalletV2BankingAddInputSchema = z.object({
  bank_name: z.string().min(1, "bank_name is required"),
  bank_account: z.string().min(1, "bank_account is required"),
  account_nickname: z.string().optional(),
  asset: z.string().optional(),
  source: z.string().optional(),
});

export const WalletV2BankingUuidInputSchema = z.object({
  bank_uuid: z.string().min(1, "bank_uuid is required"),
});

export const WalletV2BankingFavoriteInputSchema = z.object({
  bank_uuid: z.string().min(1, "bank_uuid is required"),
  is_favorite: z.boolean(),
});

export const WalletV2BankingEditInputSchema = z.object({
  bank_uuid: z.string().min(1, "bank_uuid is required"),
  bank_name: z.string().min(1, "bank_name is required"),
  bank_account: z.string().min(1, "bank_account is required"),
  account_nickname: z.string().optional(),
  asset: z.string().optional(),
  source: z.string().optional(),
});

// ─── Wallet V2: Referral ───────────────────────────────────────

export const WalletV2ReferralInputSchema = z.object({
  referral_code: z.string().min(1, "Referral code is empty."),
});

export const WalletV2ReferralSubmitResponseSchema = z.object({
  referred_user: z.string(),
  referral_code: z.string(),
});

export const WalletV2ReferralDetailsResponseSchema = z.object({
  referral_code: z.string(),
  reward_amount: z.number(),
  limit: z.number(),
  used: z.number(),
  program_referrer_reward_amount: z.number(),
  program_minimum_top_up_amount: z.number(),
  description: z.string(),
});

export const WalletV2ReferralJourneyStepSchema = z.object({
  title: z.string(),
  description: z.string(),
  icon_url: z.string(),
  navigate_to: z.string(),
  value: z.number(),
  unit: z.string(),
});

export const WalletV2ReferralJourneyResponseSchema = z.object({
  referral_eligibility: z.boolean(),
  steps: z.array(WalletV2ReferralJourneyStepSchema),
  steps_completed: z.number(),
  max_steps: z.number(),
  program_referee_reward_amount: z.number(),
  minimum_top_up: z.number(),
});

// ─── Wallet V2: Pay Bank Transfer ──────────────────────────────

export const WalletV2BankTransferFeeResponseSchema = z.object({
  fee: z.string(),
  ui_fee: z.number(),
});

export const WalletV2PayBankTransferInitiateInputSchema = z.object({
  bank_uuid: z.string().min(1, "bank_uuid is required"),
  memo: z.string().optional(),
  captcha_key: z.string().optional(),
  captcha_value: z
    .object({
      slide_x: z.number(),
      slide_y: z.number(),
    })
    .optional(),
});

export const WalletV2PayBankTransferSendInputSchema = z.object({
  uuid: z.string().min(1, "uuid is required"),
  data: z.string().min(1, "data is required"),
});
export const WalletV2PayBankTransferSendResponseSchema = z
  .object({
    uuid: z.string(),
    status: z.string(),
    activity: z.object({
      uuid: z.string(),
    }),
    activity_uuid: z.string().optional(),
  })
  .passthrough();

export const WalletV2PayBankTransferInitiateResponseSchema = z
  .object({
    uuid: z.string(),
    status: z.string(),
    activity: z
      .object({
        uuid: z.string(),
        owner: z.string(),
        date_created: z.string(),
        source: z.string(),
      })
      .passthrough(),
    temp_wallet: z
      .object({
        uuid: z.string(),
        public_address: z.string(),
        JIDR_token_address: z.string(),
        state: z.string(),
        failure_operation: z.string().nullable(),
        error_description: z.string().nullable(),
        tx_hash_close: z.string().nullable(),
      })
      .passthrough(),
    bank: z
      .object({
        account_holder: z.string().nullable().optional(),
        account_number: z.string(),
        bank_name: z.string(),
      })
      .passthrough(),
  })
  .passthrough();

// ─── Wallet V2: Transfer ───────────────────────────────────────

export const WalletV2TransferInitiateInputSchema = z.object({
  destination_address: z.string().min(1, "destination_address is required"),
  ui_amount: z.union([z.number(), z.string()]),
  address_name: z.string().optional(),
  save_destination: z.boolean().optional(),
  captcha_key: z.string().optional(),
  captcha_value: z
    .object({
      slide_x: z.number(),
      slide_y: z.number(),
    })
    .optional(),
});

export const WalletV2TransferSendInputSchema = z.object({
  uuid: z.string().min(1, "uuid is required"),
  data: z.string().min(1, "data is required"),
  save_destination: z.boolean().optional(),
  // Normalize boolean false/null to undefined — mobile@0.8.6 sent address_name: false
  // when save_destination=false, causing a 400 before the handler ran.
  address_name: z.preprocess(
    (val) => (val === false || val === null ? undefined : val),
    z.string().optional()
  ),
});

export const WalletV2TransferInitiateResponseSchema = z.union([
  z
    .object({
      uuid: z.string(),
      seqNo: z.number().optional(),
      serializedTransaction: z.string(),
      captcha_required: z.literal(false).optional(),
    })
    .passthrough(),
  CaptchaRequiredResponseSchema,
]);

export const WalletV2TransferSendResponseSchema = z
  .object({
    signature: z.string().nullable(),
    amount: z.union([z.string(), z.number()]),
    message: z.string(),
    uuid: z.string(),
    activity: z.object({
      uuid: z.string(),
    }),
    activity_uuid: z.string().optional(),
  })
  .passthrough();

// ─── Wallet V2: KYC ────────────────────────────────────────────

export const WalletV2KycAccessTokenInputSchema = z.object({
  levelName: z.string().min(1, "levelName is required"),
});

export const WalletV2KycAccessTokenResponseSchema = z.object({
  token: z.string(),
  userId: z.string(),
});
export const WalletV2KycVerifiedItemsSchema = z.object({
  phone_number: z.boolean(),
  email: z.boolean(),
  id_card: z.boolean(),
  selfie: z.boolean(),
  source_of_wealth: z.boolean(),
});

export const WalletV2KycDetailResponseSchema = z.object({
  kyc_user_uuid: z.string(),
  sumsub_uuid: z.string(),
  kyc_level: z.string(),
  verified_items: WalletV2KycVerifiedItemsSchema,
  phone_number: z.string().nullable().optional(),
  email: z.string(),
  full_legal_name: z.string(),
  review_status: z.string(),
});

export const WalletV2KycBenefitDetailSchema = z.object({
  title: z.string(),
  value: z.number(),
  unit: z.string(),
});

export const WalletV2KycLevelStatusSchema = z.object({
  isActive: z.boolean(),
  title: z.string(),
  subTitle: z.string(),
});

export const WalletV2KycBenefitSchema = z.object({
  kycLevel: z.string(),
  kycLevelName: z.string(),
  kycLevelStatus: WalletV2KycLevelStatusSchema,
  kycBenefitDetail: z.array(WalletV2KycBenefitDetailSchema),
});

export const WalletV2KycBenefitsResponseSchema = z.array(
  WalletV2KycBenefitSchema
);

// ─── Wallet V2: Virtual Account ────────────────────────────────

export const WalletV2VirtualAccountInputSchema = z.object({
  bank_code: z
    .string({ required_error: "bank_code is required" })
    .min(1, "bank_code is required"),
  ui_amount: z.union([z.number(), z.string()]).optional(),
});

export const WalletV2VirtualAccountProviderSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  display_name: z.string(),
  max_amount: z.number(),
  min_amount: z.number(),
  icon_url: z.string(),
  cut_off_time: z
    .object({
      start: z.string(),
      end: z.string(),
    })
    .optional(),
  is_default_fallback: z.boolean().optional(),
});
export const WalletV2VirtualAccountSupportedResponseSchema = z.array(
  WalletV2VirtualAccountProviderSchema
);

export const WalletV2VirtualAccountInstructionSchema = z.object({
  title: z.string(),
  unordered: z.boolean(),
  content: z.array(z.string()),
});

export const WalletV2VirtualAccountTopUpInstructionResponseSchema = z.record(
  z.array(WalletV2VirtualAccountInstructionSchema)
);

export const WalletV2VirtualAccountCreateResponseSchema = z.union([
  z.object({
    id: z.string(),
    bank_code: z.string(),
    account_number: z.string(),
    expiry_date: z.string(),
    ui_amount: z.number(),
  }),
  z.object({}).strict(),
]);

// ─── Wallet V2: PPOB Payment Status ────────────────────────────

export const WalletV2PpobPaymentStatusInputSchema = z.object({
  reference: z.string().min(1, "reference is required"),
});

// ─── Admin V2: Redeem ──────────────────────────────────────────

export const AdminV2RedeemUuidInputSchema = z.object({
  uuid: z.string().min(1, "UUID is required"),
});

export const AdminV2RedeemRejectInputSchema = z.object({
  uuid: z.string().min(1, "UUID is required"),
  rejection_reason: z.string().optional(),
  rejection_detail: z.string().optional(),
});

// ─── Admin V2: Spread ──────────────────────────────────────────

export const AdminV2SpreadAddInputSchema = z.object({
  source_asset_id: z.string().min(1, "source_asset_id is required"),
  destination_asset_id: z.string().min(1, "destination_asset_id is required"),
  spread_in_bps: z.number(),
});

export const AdminV2SpreadEditInputSchema = z.object({
  uuid: z.string().min(1, "UUID is required"),
  spread_in_bps: z.number(),
});

export const AdminV2SpreadBulkEditInputSchema = z.object({
  spread_in_bps: z.number(),
  uuids: z.array(z.string().min(1)),
});

// ─── Admin V2: User Limit ──────────────────────────────────────

export const AdminV2UserLimitAddInputSchema = z.object({
  public_address: z.string().min(1, "public_address is required"),
  interval: z.string().min(1, "interval is required"),
  redeem_request_limit: z.number(),
  redeem_to_crypto_limit: z.number(),
  user_label: z.string().optional(),
});

export const AdminV2UserLimitEditInputSchema = z.object({
  id: z.coerce.number().int(),
  public_address: z.string().min(1, "public_address is required"),
  interval: z.string().min(1, "interval is required"),
  redeem_request_limit: z.number(),
  redeem_to_crypto_limit: z.number(),
});

export const AdminV2UserLimitIdInputSchema = z.object({
  id: z.coerce.number().int(),
});

// ─── Admin V2: Fireblocks V2 ───────────────────────────────────

export const AdminV2FireblocksResendByIdInputSchema = z.object({
  webhookId: z.string().min(1, "webhookId is required"),
  mode: z.string().optional(),
  fireblocksTransactionId: z.string().optional(),
  notificationId: z.string().optional(),
  idempotencyKey: z.string().optional(),
});

export const AdminV2FireblocksRefreshVaultAssetInputSchema = z.object({
  vaultAccountId: z.string().min(1, "vaultAccountId is required"),
  assetId: z.string().min(1, "assetId is required"),
});

export const AdminV2FireblocksRefreshVaultAssetResponseSchema = z.object({
  id: z.string(),
  vaultAccountId: z.string(),
  total: z.string(),
  available: z.string(),
  pending: z.string(),
  frozen: z.string(),
  lockedAmount: z.string(),
  blockHeight: z.string().optional(),
  blockHash: z.string().optional(),
});

export const AdminV2FireblocksRequeueValidationInputSchema = z.object({
  fireblocks_transaction_uuid: z
    .string()
    .min(1, "fireblocks_transaction_uuid is required"),
  reason: z.string().optional(),
});

export const AdminV2FireblocksRequeueValidationResponseSchema = z.object({
  uuid: z.string(),
  validation_status: z.string(),
  validation_retry_count: z.number(),
  last_observed_balance: z.string().nullable(),
  required_amount: z.string().nullable(),
  queued: z.boolean(),
});

// ─── Admin V2: Event Referral ──────────────────────────────────

export const AdminV2EventReferralProgramCreateInputSchema = z.object({
  program_name: z.string().min(1, "program_name is required"),
  description: z.string().optional(),
  minimum_top_up: z.number(),
  referrer_reward_amount: z.number(),
  referee_reward_amount: z.number(),
  event_start_date: z.string().nullable().optional(),
  event_end_date: z.string().nullable().optional(),
  program_type: z.string().min(1, "program_type is required"),
});

export const AdminV2EventReferralProgramIdInputSchema = z.object({
  id: z.coerce.number().int(),
});

export const AdminV2EventReferralProgramUpdateInputSchema = z.object({
  id: z.coerce.number().int(),
  program_name: z.string().optional(),
  description: z.string().optional(),
  minimum_top_up: z.number().optional(),
  referrer_reward_amount: z.number().optional(),
  referee_reward_amount: z.number().optional(),
  event_start_date: z.string().nullable().optional(),
  event_end_date: z.string().nullable().optional(),
});

export const AdminV2EventReferralCodeCreateInputSchema = z.object({
  referral_code: z.string().min(1, "referral_code is required"),
  referrer_public_address: z
    .string()
    .min(1, "referrer_public_address is required"),
  program_id: z.coerce.number().int(),
  limit: z.number().optional(),
  expiry_date: z.string().nullable().optional(),
});

export const AdminV2EventReferralCodeUpdateInputSchema = z.object({
  id: z.coerce.number().int(),
  limit: z.number().optional(),
  is_active: z.boolean().optional(),
  expiry_date: z.string().nullable().optional(),
});

export const AdminV2EventReferralCodeIdInputSchema = z.object({
  id: z.coerce.number().int(),
});

// ─── Admin V2: Banking Management ──────────────────────────────

export const AdminV2BankingAddInputSchema = z.object({
  public_address: z.string().min(1, "public_address is required"),
  bank_name: z.string().min(1, "bank_name is required"),
  bank_account: z.string().min(1, "bank_account is required"),
  account_nickname: z.string().min(1, "account_nickname is required"),
  asset: z.string().min(1, "asset is required"),
});

export const AdminV2BankingDeleteInputSchema = z.object({
  public_address: z.string().min(1, "public_address is required"),
  bank_uuid: z.string().min(1, "bank_uuid is required"),
});

// ─── Admin V2: Spam Top-up ─────────────────────────────────────

export const AdminV2SpamTopupDeleteInputSchema = z.object({
  transaction_uuids: z.array(z.string().min(1)),
});

// ─── Open V2 ───────────────────────────────────────────────────

export const OpenV2TransactionInputSchema = z.object({
  index: z.coerce.number().int(),
});

export const OpenV2QuoteInputSchema = z.object({
  from_asset: z.string().optional(),
  to_asset: z.string().optional(),
  from_amount: z.string().optional(),
  to_amount: z.string().optional(),
  quote_uuid: z.string().optional(),
});

// ─── Callbacks ─────────────────────────────────────────────────

export const CallbackFireblocksV2InputSchema = z.object({
  token: z.string().min(1, "token is required"),
});

export const CallbackGenericInputSchema = z.any();

// ─── System ────────────────────────────────────────────────────

export const SystemUpdateStatusInputSchema = z.object({
  subsystem: z.string().min(1, "subsystem is required"),
  status: z.string().min(1, "status is required"),
  warning_text: z.string().optional(),
  manual_override: z.boolean().optional(),
});

// ─── Events ────────────────────────────────────────────────────

export const EventRedeemRequestReceivedInputSchema = z
  .object({
    uuid: z.string().min(1, "UUID is required"),
    data: z.unknown().optional(),
    source: z.string().optional(),
    network: z.string().optional(),
  })
  .passthrough();

export const EventDurianpayStatusPoolInputSchema = z
  .object({
    uuid: z.string().optional(),
    durianpay_batch_id: z.unknown().optional(),
    redeem_request_uuid: z.unknown().optional(),
  })
  .passthrough();

// ─── Dashboard / Admin UI shared DTOs ──────────────────────────

export const DashboardNumericSchema = z.union([z.number(), z.string()]);

export const CtfSummaryDataSchema = z.object({
  count: z.string(),
  total_settlement_amount: z.string(),
  total_blockchain_fee: z.string(),
  disbursement_amount: z.string().optional(),
});

export const FtcSummaryRowSchema = z.object({
  asset_id: z.string(),
  asset_name: z.string(),
  total_settlement_amount: z.string(),
  total_fees: z.string(),
  total_settlement_amount_idr: z.string(),
});

export const CryptoToFiatBulkFormSchema = z.object({
  uuids: z.array(z.string()),
  new_status: z.string(),
  redeem_request_uuid: z.string().optional(),
  deposit_returned_date: z.string().optional(),
  return_proof_url: z.string().optional(),
});

export const FiatToCryptoBulkFormSchema = z.object({
  uuids: z.array(z.string()),
  new_status: z.string(),
  disbursement_address: z.string().optional(),
  disbursement_proof_url: z.string().optional(),
});

const dashboardNullish = <T extends z.ZodTypeAny>(schema: T) =>
  z.preprocess(
    (value) => (value === null ? undefined : value),
    schema.optional()
  );

const dashboardNullishObject = <T extends z.ZodTypeAny>(schema: T) =>
  z.preprocess((value) => (value == null ? {} : value), schema);

const DashboardOptionalStringSchema = dashboardNullish(z.string());
const DashboardOptionalNumericSchema = dashboardNullish(DashboardNumericSchema);

const DashboardAmountSchema = z.union([
  DashboardNumericSchema,
  z.object({
    selling_price: DashboardOptionalNumericSchema,
  }),
]);

const DashboardActivitySchema = z
  .object({
    source: DashboardOptionalStringSchema,
    date_created: DashboardOptionalStringSchema,
  })
  .passthrough()
  .catch({});

const DashboardEwalletSchema = z
  .object({
    label: DashboardOptionalStringSchema,
    phone_number: DashboardOptionalStringSchema,
    provider: DashboardOptionalStringSchema,
  })
  .passthrough()
  .catch({});

const DashboardBankingSchema = z
  .object({
    account_holder: DashboardOptionalStringSchema,
    account_number: DashboardOptionalStringSchema,
    bank_name: DashboardOptionalStringSchema,
  })
  .passthrough()
  .catch({});

const DashboardDepositSchema = z
  .object({
    address: DashboardOptionalStringSchema,
    asset_id: DashboardOptionalStringSchema,
  })
  .passthrough()
  .catch({});

const DashboardVirtualAccountSchema = z
  .object({
    bank_code: DashboardOptionalStringSchema,
    account_number: DashboardOptionalStringSchema,
  })
  .passthrough()
  .catch({});

const DashboardTempWalletSchema = z
  .object({
    address: DashboardOptionalStringSchema,
  })
  .passthrough()
  .catch({});

const DashboardProductSchema = z
  .object({
    product_name: DashboardOptionalStringSchema,
  })
  .passthrough()
  .catch({});

const DashboardExtraSchema = z
  .object({
    amount: DashboardOptionalNumericSchema,
    asset_name: DashboardOptionalStringSchema,
  })
  .passthrough()
  .catch({});

export const DashboardRowSchema = z
  .object({
    uuid: z.string(),
    public_address: DashboardOptionalStringSchema,
    ton_address: DashboardOptionalStringSchema,
    created_at: DashboardOptionalStringSchema,
    status: DashboardOptionalStringSchema,
    ui_amount: DashboardOptionalNumericSchema,
    idr_amount: DashboardOptionalNumericSchema,
    amount: dashboardNullish(DashboardAmountSchema),
    bank_code: DashboardOptionalStringSchema,
    bank_account: DashboardOptionalStringSchema,
    ewallet_provider: DashboardOptionalStringSchema,
    phone_number: DashboardOptionalStringSchema,
    updated_at: DashboardOptionalStringSchema,
    account_type: DashboardOptionalStringSchema,
    target_address: DashboardOptionalStringSchema,
    maker: DashboardOptionalStringSchema,
    tx_status: DashboardOptionalStringSchema,
    date_requested: DashboardOptionalStringSchema,
    settlement_amount: DashboardOptionalNumericSchema,
    crypto_disbursement_address: DashboardOptionalStringSchema,
    va_account_number: DashboardOptionalStringSchema,
    disbursement_asset_id: DashboardOptionalStringSchema,
    expected_amount: DashboardOptionalNumericSchema,
    merchant_uuid: DashboardOptionalStringSchema,
    customer_no: DashboardOptionalStringSchema,
    order_type: DashboardOptionalStringSchema,
    source_type: DashboardOptionalStringSchema,
    from_network: DashboardOptionalStringSchema,
    to_network: DashboardOptionalStringSchema,
    transfer_from: DashboardOptionalStringSchema,
    source_asset_id: DashboardOptionalStringSchema,
    destination_asset_id: DashboardOptionalStringSchema,
    spread_in_bps: DashboardOptionalNumericSchema,
    asset_id: DashboardOptionalStringSchema,
    svg: DashboardOptionalStringSchema,
    activity: dashboardNullishObject(DashboardActivitySchema),
    ewallet: dashboardNullishObject(DashboardEwalletSchema),
    banking: dashboardNullishObject(DashboardBankingSchema),
    deposit: dashboardNullishObject(DashboardDepositSchema),
    virtual_account: dashboardNullishObject(DashboardVirtualAccountSchema),
    temp_wallet: dashboardNullishObject(DashboardTempWalletSchema),
    product: dashboardNullish(DashboardProductSchema),
    extra: dashboardNullishObject(DashboardExtraSchema),
  })
  .passthrough();
